import { slicedToArray as _slicedToArray } from './_virtual/_rollupPluginBabelHelpers.js';
import './node_modules/.pnpm/core-js@3.27.1/node_modules/core-js/modules/es.array.iterator.js';
import './node_modules/.pnpm/core-js@3.27.1/node_modules/core-js/modules/es.object.to-string.js';
import './node_modules/.pnpm/core-js@3.27.1/node_modules/core-js/modules/es.set.constructor.js';
import './node_modules/.pnpm/core-js@3.27.1/node_modules/core-js/modules/es.string.iterator.js';
import './node_modules/.pnpm/core-js@3.27.1/node_modules/core-js/modules/web.dom-collections.iterator.js';
import './node_modules/.pnpm/core-js@3.27.1/node_modules/core-js/modules/es.array.from.js';
import './node_modules/.pnpm/core-js@3.27.1/node_modules/core-js/modules/es.json.stringify.js';
import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/shim/with-selector';
import { InMemoryData, goodTry, parseJSON } from './utils/storage.js';

var inMemoryData = new InMemoryData();
var isBrowser = typeof window !== 'undefined';
function useLocalStorage(key, options) {
  var _useState = useState(options === null || options === void 0 ? void 0 : options.defaultValue),
    _useState2 = _slicedToArray(_useState, 1),
    defaultValue = _useState2[0];
  if (!isBrowser) {
    return [defaultValue, function () {}, {
      isPersistent: true,
      removeItem: function removeItem() {}
    }];
  }
  var serializer = options === null || options === void 0 ? void 0 : options.serializer;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useBrowserStorage(window.localStorage, key, defaultValue, options === null || options === void 0 ? void 0 : options.storageSync, serializer === null || serializer === void 0 ? void 0 : serializer.parse, serializer === null || serializer === void 0 ? void 0 : serializer.stringify);
}
function useSessionStorage(key, options) {
  var _useState3 = useState(options === null || options === void 0 ? void 0 : options.defaultValue),
    _useState4 = _slicedToArray(_useState3, 1),
    defaultValue = _useState4[0];
  if (!isBrowser) {
    return [defaultValue, function () {}, {
      isPersistent: true,
      removeItem: function removeItem() {}
    }];
  }
  var serializer = options === null || options === void 0 ? void 0 : options.serializer;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useBrowserStorage(window.sessionStorage, key, defaultValue, options === null || options === void 0 ? void 0 : options.storageSync, serializer === null || serializer === void 0 ? void 0 : serializer.parse, serializer === null || serializer === void 0 ? void 0 : serializer.stringify);
}

// listeners
var callbacks = new Set();

// trigger all listeners
function triggerCallbacks(key) {
  for (var _i = 0, _Array$from = Array.from(callbacks); _i < _Array$from.length; _i++) {
    var callback = _Array$from[_i];
    callback(key);
  }
}
function useBrowserStorage(storage, key, defaultValue) {
  var storageSync = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : true;
  var parse = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : parseJSON;
  var stringify = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : JSON.stringify;
  if (!inMemoryData.has(key) && defaultValue !== undefined && goodTry(function () {
    return storage.getItem(key);
  }) === null) {
    goodTry(function () {
      return storage.setItem(key, typeof defaultValue === 'string' ? defaultValue : stringify(defaultValue));
    });
  }
  var storageValue = useRef({
    item: null,
    parsed: defaultValue
  });
  var value = useSyncExternalStoreWithSelector(useCallback(function (onStoreChange) {
    var onChange = function onChange(localKey) {
      if (key === localKey) {
        onStoreChange();
      }
    };
    callbacks.add(onChange); // subscribe
    return function () {
      callbacks.delete(onChange); // unsubscribe
    };
  }, [key]), function () {
    var _goodTry;
    var item = (_goodTry = goodTry(function () {
      return storage.getItem(key);
    })) !== null && _goodTry !== void 0 ? _goodTry : null;
    if (inMemoryData.has(key)) {
      storageValue.current = {
        item: item,
        parsed: inMemoryData.get(key)
      };
    } else if (item !== storageValue.current.item) {
      var parsed;
      try {
        parsed = item === null ? defaultValue : parse(item);
      } catch (_unused) {
        parsed = defaultValue || item;
      }
      storageValue.current = {
        item: item,
        parsed: parsed
      };
    }
    return storageValue.current.parsed;
  }, function () {
    return defaultValue;
  }, function (state) {
    return state;
  }, function (a, b) {
    return JSON.stringify(a) === JSON.stringify(b);
  });
  var setState = useCallback(function (newValue) {
    var nextValue = newValue instanceof Function ? newValue(storageValue.current.parsed) : newValue;
    try {
      storage.setItem(key, typeof nextValue === 'string' ? nextValue : stringify(nextValue));
      inMemoryData.delete(key);
    } catch (_unused2) {
      inMemoryData.set(key, nextValue);
    }
    triggerCallbacks(key);
  }, [key, storage, stringify]);
  useEffect(function () {
    if (!storageSync) {
      return;
    }
    var onStorage = function onStorage(e) {
      if (e.storageArea === goodTry(function () {
        return storage;
      }) && e.key === key) {
        triggerCallbacks(key);
      }
    };
    window.addEventListener('storage', onStorage);
    return function () {
      return window.removeEventListener('storage', onStorage);
    };
  }, [key, storage, storageSync]);
  return useMemo(function () {
    return [value, setState, {
      isPersistent: value === defaultValue || !inMemoryData.has(key),
      removeItem: function removeItem() {
        goodTry(function () {
          return storage.removeItem(key);
        });
        inMemoryData.delete(key);
        triggerCallbacks(key);
      }
    }];
  }, [value, setState, defaultValue, key, storage]);
}

export { useLocalStorage, useSessionStorage };
